// @ts-nocheck
import { createSignal, createMemo, createResource, createEffect, onMount, onCleanup } from "solid-js";
import * as d3 from "d3";
import { sourcesStore } from "../../../store/sourcesStore";
import { selectedTime, isPlaying, setSelectedTime, setIsManualTimeChange, isManualTimeChange, requestTimeControl, releaseTimeControl, timeWindow, setTimeWindow, isUnsetPlaybackTime } from "../../../store/playbackStore";
import { unifiedDataStore } from "../../../store/unifiedDataStore";
import { persistantStore } from "../../../store/persistantStore";
import { selectedRange, setSelectedRange, setHasSelection, setIsCut, cutEvents, setSelectedRanges,  setSelectedEvents } from "../../../store/selectionStore";
import { selectedRacesTimeseries as globalSelectedRaces, selectedLegsTimeseries as globalSelectedLegs, raceOptions } from "../../../store/filterStore";
import { debug, warn as logWarn, error as logError } from "../../../utils/console";
import { getData } from "../../../utils/global";
import { themeStore } from "../../../store/themeStore";
import { defaultChannelsStore } from "../../../store/defaultChannelsStore";
import { formatTime } from "../../../utils/global";
import { getCurrentDatasetTimezone } from "../../../store/datasetTimezoneStore";
import { repositionFleetPlaybackForFilteredData, fleetTimeWindowAfterBrush, FLEET_PLAYBACK_START_OFFSET_MS } from "../../../utils/fleetTimeWindow";
import { mountTimeWindowBand, type TimeWindowBandHandle } from "./utils/timeWindowBand";
import { TIMELINE_VMG_GAP_THRESHOLD_MS, REFERENCE_DELTA_WARMUP_MS } from "./utils/vmgReferenceDelta";
import { vmgDeltaTask, computeReferenceVmgDeltaGroups } from "./utils/vmgReferenceDeltaTask";
import { createParallel } from "../../../utils/parallel/poolManager";

/** For logging only — never throws (unlike Date#toISOString on invalid dates). */
function safeUtcDayFromDate(d: Date | null | undefined): string | undefined {
  if (!d || !Number.isFinite(d.getTime())) {return undefined;}
  try {
    return d.toISOString().slice(0, 10);
  } catch {
    return undefined;
  }
}

/**
 * Parse persistantStore selectedDate (YYYY-MM-DD, YYYYMMDD, YYYY/MM/DD) to a local-midnight Date for calendar comparison.
 * `new Date('20260417')` is Invalid Date in JS; this avoids that.
 */
function parseDayCalendarLocal(day: string): Date | null {
  const s = String(day).trim();
  if (!s) {return null;}
  const compact = s.replace(/[-/]/g, '');
  if (/^\d{8}$/.test(compact)) {
    const y = parseInt(compact.slice(0, 4), 10);
    const m = parseInt(compact.slice(4, 6), 10);
    const d = parseInt(compact.slice(6, 8), 10);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {return null;}
    const dt = new Date(y, m - 1, d);
    return Number.isFinite(dt.getTime()) ? dt : null;
  }
  const dt = new Date(s);
  return Number.isFinite(dt.getTime()) ? dt : null;
}

interface MultiMapTimeSeriesProps {
  samplingFrequency?: number;
  onStableSelectedTimeChange?: (time: Date) => void;
  onMapUpdate?: () => void;
  /** Set of enabled source IDs, or accessor so effects/memos re-run when selection changes (e.g. from Map Settings) */
  selectedSourceIds?: Set<number> | (() => Set<number>);
  /**
   * Reference boat source id (or accessor). When present and found in the data, the timeline switches
   * into "VMG delta (m)" mode: each boat's cumulative VMG-made-good meters relative to this boat.
   * `null`/0/undefined => normal channel mode.
   */
  referenceSourceId?: number | null | (() => number | null);
  /** Source IDs to highlight (hovered + selected boats from FleetMap); paths dim when non-empty */
  highlightedSourceIds?: Set<number>;
  /** When true, only show timeline within video (media) windows; requires media fetch for selected sources */
  videoOnly?: boolean;
  /** When false, do not show brush/range UI (only playhead and click-to-seek). Default true. */
  brushEnabled?: boolean;
  /** Called when timeline loading state changes (e.g. for parent to show a waiting overlay). */
  onLoadingChange?: (loading: boolean) => void;
  [key: string]: any;
}

/**
 * MultiMapTimeSeries is used in two isolated contexts:
 * - Map context (FleetMap/MapContainer): videoOnly false/undefined, onMapUpdate drives map track data.
 *   Brush and selection updates affect the map; data is sent via onMapUpdate.
 * - Video context (FleetVideo): videoOnly true, brushEnabled false, onMapUpdate no-op.
 *   Timeline-only; no map to update. We never call onMapUpdate when videoOnly is true so FleetMap is never affected.
 */

interface Dimensions {
  width: number;
  height: number;
}

export default function MultiMapTimeSeries(props: MultiMapTimeSeriesProps) {
  // Don't destructure props to maintain reactivity!
  const { onStableSelectedTimeChange, onMapUpdate } = props;

  /** When true, this instance is timeline-only (e.g. FleetVideo). Never send data to a map. */
  const isVideoContext = (): boolean => !!props.videoOnly;
  /** Only invoke onMapUpdate when used in map context; keeps FleetMap isolated from FleetVideo usage. */
  const safeOnMapUpdate = (data: any[]): void => {
    if (!isVideoContext() && typeof onMapUpdate === 'function') {onMapUpdate(data);}
  };

  const initializeSelectedTimeFromData = (data: any[]) => {
    if (!Array.isArray(data) || data.length === 0 || isPlaying()) {return;}
    if (!isUnsetPlaybackTime(selectedTime())) {return;}

    let minTime = Infinity;
    for (const d of data) {
      const t = new Date(d.Datetime || d.timestamp || d.time).getTime();
      if (Number.isFinite(t) && !isUnsetPlaybackTime(new Date(t)) && t < minTime) {
        minTime = t;
      }
    }
    if (!Number.isFinite(minTime)) {return;}

    // Fixed 2-minute offset into the data regardless of the user's time window, so the playhead lands
    // where the reference-delta trace starts (see FLEET_PLAYBACK_START_OFFSET_MS).
    const targetMs = minTime + FLEET_PLAYBACK_START_OFFSET_MS;

    if (requestTimeControl('multimaptimeseries')) {
      const targetTime = new Date(targetMs);
      setIsManualTimeChange(true);
      setSelectedTime(targetTime, 'multimaptimeseries');
      if (onStableSelectedTimeChange) {
        onStableSelectedTimeChange(targetTime);
      }
      setTimeout(() => releaseTimeControl('multimaptimeseries'), 100);
    }
  };

  debug('🕐 MultiMapTimeSeries: Component initialized');
  
  let chartContainer: HTMLElement | null = null;
  let svg: any = null;
  let xScale: any = null;
  let yScale: any = null;
  let brush: any = null;
  let brushGroup: any = null;
  let playheadGroup: any = null;
  /** The time window drawn as a draggable band; remounted with the svg (initSVG). */
  let timeWindowBand: TimeWindowBandHandle | null = null;
  const [timeWindowBandEpoch, setTimeWindowBandEpoch] = createSignal(0);

  // Brush state management
  let isBrushActive = false;
  let isClearingBrush = false;
  let isProgrammaticallyUpdatingBrush = false;
  let brushTimeout: ReturnType<typeof setTimeout> | null = null;
  let lastFilterSignature = ''; // Track filter changes to only reset time when filters actually change
  let lastRaceLegSignature = ''; // Race/leg-only changes reposition playback for split fleet reports
  let lastMapUpdateSignature: string | null = null; // Track map update signatures to prevent duplicate updates
  let prevSelectedTime: Date | null = null; // Track previous selected time to detect large jumps
  
  const [dimensions, setDimensions] = createSignal<Dimensions>({ width: 0, height: 0 });
  const [combinedData, setCombinedData] = createSignal<any[]>([]);
  const [mediaWindows, setMediaWindows] = createSignal<any[]>([]);
  const margin = { top: 10, right: 10, bottom: 30, left: 50 };

  // Get timezone for axis formatting
  const getTimezone = () => {
    const tz = getCurrentDatasetTimezone();
    // Always log (removed throttle for debugging)
    debug('🕐 MultiMapTimeSeries.getTimezone: Called', {
      timezone: tz,
      timezoneType: typeof tz,
      isNull: tz === null,
      isUndefined: tz === undefined,
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack?.split('\n').slice(1, 4).join('\n')
    });
    return tz;
  };

  const sourceKey = (d: any): number | undefined => d?.source_id;

  /** Resolve selectedSourceIds from prop (accessor or value) so effects/memos track and re-run when Map Settings change selection. */
  const getSelectedSourceIds = (): Set<number> => {
    const raw = props.selectedSourceIds;
    if (typeof raw === 'function') {return raw() || new Set();}
    return raw || new Set();
  };

  // Get dynamic channel names from store
  const { bspName, twsName, vmgName, vmgPercName } = defaultChannelsStore;

  // Helper function to get channel name based on maptype (similar to MapTimeSeries)
  const getChannelName = (): string => {
    const maptype = props.maptype;
    const tws = twsName() || 'Tws_kts';
    const bsp = bspName() || 'Bsp_kts';
    const vmgPerc = vmgPercName() || 'Vmg_perc';
    const vmg = vmgName() || 'Vmg';
    
    if (maptype === "WIND") {
      return tws;
    } else if (maptype === "VMG%") {
      return vmgPerc;
    } else if (maptype === "VMG") {
      return vmg;
    } else {
      // DEFAULT or undefined - use Bsp
      return bsp;
    }
  };

  // Helper function to get Bsp value with case-insensitive fallback
  const getBsp = (d: any): number => {
    if (!d) {return 0;}
    const bspField = bspName();
    const val = d[bspField] ?? d[bspField.toLowerCase()] ?? d[bspField.toUpperCase()] ?? d.Bsp ?? d.bsp ?? 0;
    const numVal = Number(val);
    return isNaN(numVal) ? 0 : numVal;
  };

  // Media windows for videoOnly mode (same API as MapTimeSeries)
  const fetchMediaSources = async (dateYmd: string) => {
    try {
      const cls = persistantStore.selectedClassName && persistantStore.selectedClassName();
      const proj = persistantStore.selectedProjectId && persistantStore.selectedProjectId();
      if (!cls || !proj) {return [];}
      const url = `/api/media/sources?class_name=${encodeURIComponent(cls)}&project_id=${encodeURIComponent(proj)}&date=${encodeURIComponent(dateYmd)}`;
      debug('MultiMapTimeSeries: Fetching media sources from:', url);
      const response = await getData(url);
      if (!response.success || response.data == null) {return [];}
      const list = Array.isArray(response.data) ? response.data : [];
      return list.map((r: any, i: number) => ({
        id: r.id || r.media_source || r.name || `src_${i}`,
        name: r.name || r.media_source || r.id || `Source ${i + 1}`,
      }));
    } catch (error: unknown) {
      logError('MultiMapTimeSeries: Error fetching media sources', error as Error);
      return [];
    }
  };

  const fetchMediaForSource = async (sourceId: string | number, dateYmd: string) => {
    try {
      const cls = persistantStore.selectedClassName && persistantStore.selectedClassName();
      const proj = persistantStore.selectedProjectId && persistantStore.selectedProjectId();
      if (!cls || !proj) {return [];}
      const url = `/api/media?class_name=${encodeURIComponent(cls)}&project_id=${encodeURIComponent(proj)}&date=${encodeURIComponent(dateYmd)}&media_source=${encodeURIComponent(String(sourceId))}`;
      const response = await getData(url);
      if (!response.success || response.data == null) {return [];}
      const list = Array.isArray(response.data) ? response.data : [];
      return list.map((r: any) => {
        const start = r.start_time || r.start || r.begin || r.ts_start;
        const end = r.end_time || r.end || r.finish || r.ts_end;
        const startDate = start ? new Date(start) : null;
        const endDate = end ? new Date(end) : null;
        if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {return null;}
        return { start: startDate, end: endDate, ...r };
      }).filter(Boolean);
    } catch (error: unknown) {
      logError('MultiMapTimeSeries: Error fetching media for source', error as Error);
      return [];
    }
  };

  const fetchMediaDataForSelectedSources = async (dateYmd: string) => {
    const currentIds = getSelectedSourceIds();
    const selected = currentIds instanceof Set ? currentIds : new Set<number>();
    if (selected.size === 0) {
      setMediaWindows([]);
      return;
    }
    try {
      const sources = await fetchMediaSources(dateYmd);
      if (sources.length === 0) {
        setMediaWindows([]);
        return;
      }
      const allWindows: any[] = [];
      for (const ms of sources) {
        const msIdStr = String(ms.id);
        const msIdNum = Number(ms.id);
        const matchesSelected =
          (Number.isFinite(msIdNum) && selected.has(msIdNum)) ||
          Array.from(selected).some(
            (sid) =>
              sid === msIdNum ||
              sourcesStore.getSourceName(sid) === msIdStr ||
              sourcesStore.getSourceName(sid) === (ms.name ?? '')
          );
        if (!matchesSelected) {continue;}
        const media = await fetchMediaForSource(ms.id, dateYmd);
        media.forEach((item: any) => allWindows.push({ sourceId: ms.id, ...item }));
      }
      debug('MultiMapTimeSeries: Fetched media windows for videoOnly', { count: allWindows.length });
      setMediaWindows(allWindows);
    } catch (error: unknown) {
      logError('MultiMapTimeSeries: Error fetching media data', error as Error);
      setMediaWindows([]);
    }
  };

  // Historical data lives in IndexedDB - query it directly, not through props

  // Fetch and combine data from unified store (for post-processed data)
  // Made fully async and non-blocking to improve load performance
  const fetchCombinedData = async () => {
    const onLoading = props.onLoadingChange;
    try {
      if (typeof onLoading === 'function') {onLoading(true);}
      const cls = persistantStore.selectedClassName && persistantStore.selectedClassName();
      const proj = persistantStore.selectedProjectId && persistantStore.selectedProjectId();
      // Use selectedDate from persistantStore as the source of truth (matches MapContainer)
      const day = persistantStore.selectedDate && persistantStore.selectedDate();
      if (!cls || !proj || !day) {
        debug('MultiMapTimeSeries: Missing required params', { cls, proj, day });
        setCombinedData([]);
        safeOnMapUpdate([]);
        return;
      }
      const ymd = String(day).replace(/[-/]/g, '');
      
      // Get selected source IDs to filter data fetching
      const currentSelectedIds = getSelectedSourceIds();
      const selectedIds = currentSelectedIds instanceof Set ? currentSelectedIds : undefined;
      
      // Fetch data asynchronously without blocking - use Promise to ensure it's truly async
      const fetchPromise = unifiedDataStore.fetchMapDataForDay(cls, Number(proj), ymd, selectedIds);
      
      // Allow UI to remain responsive by yielding to event loop
      // This ensures the fetch doesn't block rendering
      await new Promise(resolve => setTimeout(resolve, 0));
      
      const results = await fetchPromise;
      
      // Validate data structure and log sample
      if (Array.isArray(results) && results.length > 0) {
        const samplePoint = results[0];
        const hasSourceId = samplePoint?.source_id !== undefined || samplePoint?.sourceId !== undefined;
        const hasLat = samplePoint?.Lat_dd !== undefined || samplePoint?.lat_dd !== undefined || 
                     samplePoint?.Lat !== undefined || samplePoint?.lat !== undefined;
        const hasLng = samplePoint?.Lng_dd !== undefined || samplePoint?.lng_dd !== undefined || 
                     samplePoint?.Lng !== undefined || samplePoint?.lng !== undefined;
        
        debug('MultiMapTimeSeries: fetchMapDataForDay returned data', {
          totalRows: results.length,
          samplePoint: {
            hasSourceId,
            hasLat,
            hasLng,
            source_id: samplePoint?.source_id,
            sourceId: samplePoint?.sourceId,
            Lat_dd: samplePoint?.Lat_dd,
            Lng_dd: samplePoint?.Lng_dd,
            keys: Object.keys(samplePoint || {}).slice(0, 15)
          }
        });
        
        if (!hasSourceId) {
          logWarn('MultiMapTimeSeries: WARNING - Data missing source_id field', {
            samplePoint: samplePoint,
            allKeys: Object.keys(samplePoint || {})
          });
        }
        
        if (!hasLat || !hasLng) {
          logWarn('MultiMapTimeSeries: WARNING - Data missing lat/lng fields', {
            hasLat,
            hasLng,
            samplePoint: samplePoint,
            allKeys: Object.keys(samplePoint || {})
          });
        }
        
        // Validate that the returned data matches the requested date
        const sampleDate = samplePoint?.Datetime;
        if (sampleDate) {
          const dataDate = new Date(sampleDate);
          const requestedDate = parseDayCalendarLocal(day);
          if (!requestedDate) {
            debug('MultiMapTimeSeries: Could not parse selected day for date comparison', { day });
          } else if (!Number.isFinite(dataDate.getTime())) {
            debug('MultiMapTimeSeries: Sample Datetime not parseable for date comparison', { sampleDate });
          } else {
            const dateMatch = dataDate.getFullYear() === requestedDate.getFullYear() &&
                             dataDate.getMonth() === requestedDate.getMonth() &&
                             dataDate.getDate() === requestedDate.getDate();
            if (!dateMatch) {
              logWarn('MultiMapTimeSeries: Date mismatch detected', {
                requestedDate: day,
                dataDate: sampleDate,
                dataDateFormatted: safeUtcDayFromDate(dataDate),
                requestedDateFormatted: safeUtcDayFromDate(requestedDate)
              });
            }
          }
        }
      } else {
        debug('MultiMapTimeSeries: fetchMapDataForDay returned no data', {
          resultsType: Array.isArray(results) ? 'array' : typeof results,
          resultsLength: Array.isArray(results) ? results.length : 'N/A'
        });
      }

          // Store RAW data (no race filtering here - we'll do that reactively in a memo)
          // Just store all data for the day
          // CRITICAL: Normalize source_id field before storing to ensure consistency
          const normalizedResults = results.map(pt => {
            // Normalize source_id field name - ensure it's always 'source_id' (lowercase)
            if (pt.sourceId !== undefined && pt.source_id === undefined) {
              pt.source_id = Number(pt.sourceId);
            } else if (pt.Source_id !== undefined && pt.source_id === undefined) {
              pt.source_id = Number(pt.Source_id);
            } else if (pt.sourceID !== undefined && pt.source_id === undefined) {
              pt.source_id = Number(pt.sourceID);
            }
            // Ensure source_id is a number
            if (pt.source_id !== undefined) {
              pt.source_id = Number(pt.source_id);
            }
            return pt;
          });
          
          setCombinedData(normalizedResults);
          
          // Send data to map immediately after fetch completes
          // This ensures tracks appear even if the reactive effect hasn't triggered yet
          // Apply basic source filtering (race/leg filtering will be handled by reactive effect)
          if (normalizedResults && normalizedResults.length > 0 && !isVideoContext() && typeof onMapUpdate === 'function') {
            const currentSelectedIds = getSelectedSourceIds();
            const selected = currentSelectedIds instanceof Set ? currentSelectedIds : new Set();
            
            // Filter by selected sources if any are selected, otherwise send all data
            // MultiTrackLayer can handle empty selectedSourceIds by defaulting to all sources in data
            let filteredData = normalizedResults;
            if (selected.size > 0) {
              filteredData = normalizedResults.filter(pt => {
                const sid = Number(pt?.source_id ?? pt?.sourceId ?? pt?.Source_id ?? pt?.sourceID);
                return Number.isFinite(sid) && selected.has(sid);
              });
              debug('MultiMapTimeSeries: Filtered data by selected sources', {
                before: normalizedResults.length,
                after: filteredData.length,
                selectedSources: Array.from(selected)
              });
            } else {
              debug('MultiMapTimeSeries: No sources selected, sending all data (MultiTrackLayer will handle)', {
                totalData: normalizedResults.length
              });
            }
            
            if (filteredData && filteredData.length > 0) {
              // Validate data structure before sending - ensure all points have source_id
              const pointsWithoutSourceId = filteredData.filter(pt => 
                !pt.source_id && pt.sourceId === undefined && pt.Source_id === undefined && pt.sourceID === undefined
              );
              
              if (pointsWithoutSourceId.length > 0) {
                logWarn('MultiMapTimeSeries: WARNING - Some data points missing source_id field', {
                  totalPoints: filteredData.length,
                  pointsWithoutSourceId: pointsWithoutSourceId.length,
                  sampleMissing: pointsWithoutSourceId[0] ? Object.keys(pointsWithoutSourceId[0]).slice(0, 10) : []
                });
                // Filter out points without source_id to prevent rendering issues
                filteredData = filteredData.filter(pt => 
                  pt.source_id !== undefined || pt.sourceId !== undefined || pt.Source_id !== undefined || pt.sourceID !== undefined
                );
              }
              
              // Verify data structure before sending
              const samplePoint = filteredData[0];
              const hasSourceId = samplePoint?.source_id !== undefined || samplePoint?.sourceId !== undefined;
              const hasLat = samplePoint?.Lat_dd !== undefined || samplePoint?.lat_dd !== undefined || 
                           samplePoint?.Lat !== undefined || samplePoint?.lat !== undefined;
              const hasLng = samplePoint?.Lng_dd !== undefined || samplePoint?.lng_dd !== undefined || 
                           samplePoint?.Lng !== undefined || samplePoint?.lng !== undefined;
              
              // Get unique source IDs for logging
              const uniqueSourceIds = [...new Set(filteredData.map(pt => 
                Number(pt?.source_id ?? pt?.sourceId ?? pt?.Source_id ?? pt?.sourceID ?? 0)
              ).filter(id => Number.isFinite(id) && id > 0))];
              
              debug('MultiMapTimeSeries: Sending initial data to map after fetch', { 
                totalData: normalizedResults.length, 
                filteredData: filteredData.length,
                selectedSources: selected.size > 0 ? Array.from(selected) : 'all',
                uniqueSourceIds: uniqueSourceIds.slice(0, 10),
                sourceCount: uniqueSourceIds.length,
                samplePoint: {
                  hasSourceId,
                  hasLat,
                  hasLng,
                  source_id: samplePoint?.source_id,
                  sourceId: samplePoint?.sourceId,
                  Lat_dd: samplePoint?.Lat_dd,
                  Lng_dd: samplePoint?.Lng_dd,
                  keys: Object.keys(samplePoint || {}).slice(0, 10)
                }
              });
              
              if (!hasSourceId) {
                logWarn('MultiMapTimeSeries: WARNING - Data missing source_id field', {
                  samplePoint: samplePoint,
                  allKeys: Object.keys(samplePoint || {})
                });
              }
              
              if (!hasLat || !hasLng) {
                logWarn('MultiMapTimeSeries: WARNING - Data missing lat/lng fields', {
                  hasLat,
                  hasLng,
                  samplePoint: samplePoint,
                  allKeys: Object.keys(samplePoint || {})
                });
              }
              
              initializeSelectedTimeFromData(filteredData);
              safeOnMapUpdate(filteredData);
            } else {
              debug('MultiMapTimeSeries: No filtered data to send', {
                totalData: normalizedResults.length,
                selectedSources: selected.size > 0 ? Array.from(selected) : 'all',
                filteredDataLength: filteredData?.length || 0
              });
            }
          }
      // videoOnly mode: fetch media windows for selected sources (use selectedIds from top of fetch)
      if (props.videoOnly && selectedIds && selectedIds.size > 0) {
        await fetchMediaDataForSelectedSources(ymd);
      } else if (props.videoOnly) {
        setMediaWindows([]);
      }
    } catch (e) {
      logWarn('MultiMapTimeSeries: Fetch failed', e);
      setCombinedData([]);
      safeOnMapUpdate([]);
    } finally {
      if (typeof onLoading === 'function') {onLoading(false);}
    }
  };

  // Helper function to get filtered data (by sources, races, legs, and time window) - for use in brush handlers
  const getFilteredData = (dataOverride) => {
    const data = dataOverride || combinedData();
    const currentSelectedIds = getSelectedSourceIds();
    let selected = currentSelectedIds instanceof Set ? currentSelectedIds : new Set();
    const races = Array.isArray(props.selectedRacesTimeseries) ? props.selectedRacesTimeseries : (Array.isArray(props.selectedRaces) ? props.selectedRaces : (globalSelectedRaces && globalSelectedRaces()));
    const legs = Array.isArray(props.selectedLegsTimeseries) ? props.selectedLegsTimeseries : (Array.isArray(props.selectedLegs) ? props.selectedLegs : (globalSelectedLegs && globalSelectedLegs()));

    if (data.length === 0) {
      return [];
    }

    // Normalize source_id field before filtering to ensure consistency
    const normalizedData = data.map(pt => {
      // Normalize source_id field name - ensure it's always 'source_id' (lowercase)
      if (pt.sourceId !== undefined && pt.source_id === undefined) {
        pt.source_id = Number(pt.sourceId);
      } else if (pt.Source_id !== undefined && pt.source_id === undefined) {
        pt.source_id = Number(pt.Source_id);
      } else if (pt.sourceID !== undefined && pt.source_id === undefined) {
        pt.source_id = Number(pt.sourceID);
      }
      // Ensure source_id is a number
      if (pt.source_id !== undefined) {
        pt.source_id = Number(pt.source_id);
      }
      return pt;
    });

    // In day multi mode, empty source selection means "all sources" (same as MapContainer/MultiTrackLayer)
    if (selected.size === 0 && normalizedData.length > 0) {
      const sourcesInData = new Set<number>();
      for (const pt of normalizedData) {
        const sid = Number(sourceKey(pt));
        if (Number.isFinite(sid)) {sourcesInData.add(sid);}
      }
      if (sourcesInData.size > 0) {
        selected = sourcesInData;
        debug('MultiMapTimeSeries: Empty source selection — using all sources from data', { count: selected.size, sourceIds: Array.from(selected).slice(0, 15) });
      } else {
        return [];
      }
    }

    // First, filter by selected sources
    let filteredData = normalizedData.filter(pt => {
      const sid = Number(sourceKey(pt));
      return Number.isFinite(sid) && selected.has(sid);
    });
    

    // Then, apply race filtering if races are selected
    if (Array.isArray(races) && races.length > 0) {
      const chosen = races[0];
      
      // Validate chosen race value - skip if invalid
      if (chosen === undefined || chosen === null || chosen === '') {
        debug(`MultiMapTimeSeries: Skipping race filter - chosen race is invalid (${chosen}). Showing all data.`);
      } else {
        // First, check if the data actually has race numbers
        const raceNumbersInData = [...new Set(filteredData.map(p => {
          const rnum = Number(p.Race_number ?? p.Race ?? p.race_number);
          return Number.isFinite(rnum) ? rnum : null;
        }).filter(n => n !== null))].sort((a, b) => a - b);
        
        // Only apply race filter if the data actually has race numbers
        if (raceNumbersInData.length > 0) {
          const chosenRaceNum = chosen === 'TRAINING' ? -1 : Number(chosen);
          
          if (!Number.isFinite(chosenRaceNum)) {
            debug(`MultiMapTimeSeries: Skipping race filter - chosen race ${chosen} cannot be converted to a valid number. Showing all data.`);
          } else {
            const raceToCheck = chosen === 'TRAINING' ? -1 : chosenRaceNum;
            const beforeCount = filteredData.length;
            filteredData = filteredData.filter((p) => {
              const rnum = Number(p.Race_number ?? p.Race ?? p.race_number);
              return Number.isFinite(rnum) && rnum === raceToCheck;
            });
            if (filteredData.length === 0 && beforeCount > 0) {
              debug('MultiMapTimeSeries: Race filter removed all data', {
                chosenRace: chosen,
                raceToCheck,
                availableRaces: raceNumbersInData,
                beforeFilterCount: beforeCount,
              });
            } else if (!raceNumbersInData.includes(raceToCheck)) {
              debug('MultiMapTimeSeries: Selected race not present in data — showing empty track', {
                chosenRace: chosen,
                raceToCheck,
                availableRaces: raceNumbersInData,
              });
            }
          }
        }
      }
    }

    // Apply leg filtering if legs are selected (compare numerically: store has string[] e.g. ['1','2'])
    if (Array.isArray(legs) && legs.length > 0) {
      const legNumbers = legs.map((l) => Number(l)).filter((n) => Number.isFinite(n));
      if (legNumbers.length > 0) {
        filteredData = filteredData.filter((p) => {
          const lnum = Number(p.leg_number ?? p.Leg_number ?? p.Leg ?? p.LEG);
          return Number.isFinite(lnum) && legNumbers.includes(lnum);
        });
        debug('MultiMapTimeSeries: Leg filter applied', {
          selectedLegs: legs,
          legNumbers,
          filteredDataCount: filteredData.length
        });
      }
    }

    return filteredData;
  };


  // Track last grouped data signature to reduce logging
  let lastGroupedDataSignature = '';
  let lastFilteredDataCount = -1;
  let lastGroupedResultSignature = '';

  // Group data by source AND apply race and leg filtering
  const groupedData = createMemo(() => {
    const data = combinedData();
    const currentSelectedIds = getSelectedSourceIds();
    const selected = currentSelectedIds instanceof Set ? currentSelectedIds : new Set();
    const races = Array.isArray(props.selectedRacesTimeseries) ? props.selectedRacesTimeseries : (Array.isArray(props.selectedRaces) ? props.selectedRaces : (globalSelectedRaces && globalSelectedRaces()));
    const legs = Array.isArray(props.selectedLegsTimeseries) ? props.selectedLegsTimeseries : (Array.isArray(props.selectedLegs) ? props.selectedLegs : (globalSelectedLegs && globalSelectedLegs()));
    
    // Create signature to detect actual changes
    const signature = `${data.length}_${Array.from(selected).sort().join(',')}_${Array.isArray(races) ? races.join(',') : ''}_${Array.isArray(legs) ? legs.join(',') : ''}`;
    
    // Only log when data actually changes
    if (signature !== lastGroupedDataSignature) {
      lastGroupedDataSignature = signature;
    }

    if (data.length === 0) {
      return [];
    }

    const dataToUse = data;

    // Get filtered data (applies source, race, and leg filters; empty source selection => all sources from data)
    // Pass dataToUse directly to avoid mutating the signal (which causes infinite loops)
    let filteredData = getFilteredData(dataToUse);

    // Video-only mode: restrict to time ranges where media exists for selected sources.
    // When media windows are not loaded yet, show full filtered data so the chart is visible; once windows load we filter to video-only.
    if (props.videoOnly) {
      const windows = mediaWindows();
      if (windows.length > 0) {
        filteredData = filteredData.filter((d: any) => {
          const t = d?.Datetime ?? d?.timestamp ?? d?.time ?? d?.datetime;
          if (!t) {return false;}
          const tMs = t instanceof Date ? t.getTime() : new Date(t).getTime();
          if (!Number.isFinite(tMs)) {return false;}
          return windows.some(
            (w: any) =>
              w.start &&
              w.end &&
              tMs >= (w.start instanceof Date ? w.start.getTime() : new Date(w.start).getTime()) &&
              tMs <= (w.end instanceof Date ? w.end.getTime() : new Date(w.end).getTime())
          );
        });
        debug('MultiMapTimeSeries: videoOnly filter', { windowsCount: windows.length, resultLength: filteredData.length });
      }
      // else: keep filteredData as-is so the timeline is visible while media windows load
    }

    // Track filtered data count changes
    if (filteredData.length !== lastFilteredDataCount) {
      lastFilteredDataCount = filteredData.length;
    }

    // Now group the filtered data by source
    const groups = new Map();
    for (const pt of filteredData) {
      const sid = Number(sourceKey(pt));
      if (!Number.isFinite(sid)) {continue;}
      if (!groups.has(sid)) {groups.set(sid, []);}
      groups.get(sid).push(pt);
    }

    // Get source info from store
    const sources = sourcesStore.sources();
    const result = Array.from(groups.entries()).map(([sid, points]) => {
      const sourceInfo = sources.find(s => Number(s.source_id) === Number(sid));
      const color = sourceInfo?.color || '#1f77b4';
      const name = sourceInfo?.source_name || String(sid);
      
      // Sort by timestamp
      const sorted = [...points].sort((a, b) => {
        const aTime = new Date(a.Datetime || a.timestamp || a.time).getTime();
        const bTime = new Date(b.Datetime || b.timestamp || b.time).getTime();
        return aTime - bTime;
      });
      
      
      return { sourceId: sid, sourceName: name, color, data: sorted };
    });

    // Create signature for grouped result to reduce logging
    const resultSignature = `${result.length}_${result.map(g => `${g.sourceId}:${g.data.length}`).join(',')}`;
    if (resultSignature !== lastGroupedResultSignature) {
      lastGroupedResultSignature = resultSignature;
    }

    return result;
  });

  // ============================================================================
  // Get time extent across all sources
  // When selectedRange has one range (e.g. from FleetMap brush), use it so FleetVideo timeline stays in sync with map
  // Otherwise show all data from min to max
  // ============================================================================
  const timeExtent = createMemo(() => {
    const range = selectedRange();
    if (Array.isArray(range) && range.length === 1 && range[0]?.start_time && range[0]?.end_time) {
      const start = new Date(range[0].start_time);
      const end = new Date(range[0].end_time);
      if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && start.getTime() < end.getTime()) {
        return [start, end];
      }
    }

    const groups = groupedData();
    if (groups.length === 0) {
      // Return a valid default range (24 hours)
      const now = new Date();
      const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      return [now, tomorrow];
    }
    
    let minTime = Infinity;
    let maxTime = -Infinity;
    
    for (const group of groups) {
      for (const d of group.data) {
        const t = new Date(d.Datetime || d.timestamp || d.time).getTime();
        if (t < minTime) {minTime = t;}
        if (t > maxTime) {maxTime = t;}
      }
    }
    
    // Ensure we have valid times
    if (!Number.isFinite(minTime) || !Number.isFinite(maxTime)) {
      const now = new Date();
      const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      return [now, tomorrow];
    }
    
    // Show all data from min to max
    return [new Date(minTime), new Date(maxTime)];
  });

  // ============================================================================
  // Reference-boat VMG delta mode
  // When a reference boat is selected (and present in the data), the timeline plots each boat's
  // cumulative VMG-made-good meters relative to that boat instead of the raw channel value.
  // ============================================================================

  /** Reference boat source id normalized to a positive number, or null (accessor or value prop). */
  const getReferenceSourceId = (): number | null => {
    const raw = props.referenceSourceId;
    const v = typeof raw === 'function' ? raw() : raw;
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  /** VMG channel field used for the integration (falls back to 'Vmg'). */
  const vmgField = (): string => vmgName() || 'Vmg';

  /** True only when a reference boat is selected AND actually present among the grouped data. */
  const referenceDeltaActive = createMemo(() => {
    const refId = getReferenceSourceId();
    if (refId == null) {return false;}
    return groupedData().some((g) => Number(g.sourceId) === refId);
  });

  // The VMG reference-delta enrichment (per-boat cumulative integration + per-row interpolation over
  // the whole visible window) used to run synchronously inside this memo, blocking the main thread on
  // every grouped-data / reference-boat / time-window change. It now runs in a @nodets worker pool:
  // the reference series is built once, then every boat is enriched in parallel across cores. The pool
  // is scoped to this component and auto-disposed on cleanup; when workers are unavailable (tests, a
  // worker-hostile browser) it transparently falls back to the same computation inline on the main
  // thread. See ./utils/vmgReferenceDeltaTask.ts (self-contained twin of ./utils/vmgReferenceDelta.ts).
  const vmgPool = createParallel(vmgDeltaTask);

  /**
   * True when a brush / time-range selection is driving the window (see timeExtent()), i.e. we are NOT
   * looking at the whole unfiltered race.
   */
  const timeWindowIsFiltered = createMemo(() => {
    const range = selectedRange();
    return Array.isArray(range) && range.length > 0;
  });

  /**
   * Reactive inputs for the off-thread enrichment; null (skip) when not in reference-delta mode.
   * `t0Ms` is the head of the visible window. On the full unfiltered race the enrichment holds every
   * boat at 0 for the first REFERENCE_DELTA_WARMUP_MS (2 min) and integrates from there; when the user
   * has brushed a sub-range there is no start sequence to skip, so the delta integrates from the brush
   * start (warmup 0).
   */
  const vmgDeltaInputs = createMemo(() => {
    if (!referenceDeltaActive()) {return null;}
    const groups = groupedData();
    const refId = getReferenceSourceId();
    if (refId == null || !groups.some((g) => Number(g.sourceId) === refId)) {return null;}
    const [minTime] = timeExtent();
    const t0Ms = minTime instanceof Date ? minTime.getTime() : new Date(minTime).getTime();
    if (!Number.isFinite(t0Ms)) {return null;}
    const warmupMs = timeWindowIsFiltered() ? 0 : REFERENCE_DELTA_WARMUP_MS;
    return { groups, refId, t0Ms, vmgField: vmgField(), warmupMs };
  });

  const [vmgDeltaResource] = createResource(vmgDeltaInputs, (input) =>
    computeReferenceVmgDeltaGroups(
      vmgPool,
      input.groups as any,
      input.refId,
      input.t0Ms,
      input.vmgField,
      input.warmupMs
    )
  );

  /**
   * Grouped data used for timeline rendering. In reference-delta mode each row is enriched with
   * `_vmgDeltaM` by the worker pool; while the first result is still computing we render the raw groups
   * (delta points appear once ready). `.latest` keeps the previous enriched result on screen during a
   * recompute so switching reference boat / brushing does not flash empty.
   */
  const renderGroups = createMemo(() => {
    const groups = groupedData();
    if (!referenceDeltaActive()) {return groups;}
    const enriched = vmgDeltaResource.latest as any[] | undefined;
    return enriched && enriched.length ? enriched : groups;
  });

  /** Timeline Y value for a row: VMG delta meters in delta mode (NaN if missing), else the Bsp channel value. */
  const getTimelineYValue = (d: any): number => {
    if (referenceDeltaActive()) {
      const v = d?._vmgDeltaM;
      return typeof v === 'number' && Number.isFinite(v) ? v : NaN;
    }
    return getBsp(d);
  };

  /** Display name of the reference boat (from grouped data, else sourcesStore, else the id). */
  const getReferenceBoatName = (): string => {
    const refId = getReferenceSourceId();
    if (refId == null) {return '';}
    const grp = groupedData().find((g) => Number(g.sourceId) === refId);
    if (grp && grp.sourceName) {return String(grp.sourceName);}
    const nm = sourcesStore.getSourceName ? sourcesStore.getSourceName(refId) : '';
    return nm ? String(nm) : String(refId);
  };

  /** Y-axis label: "Distance to <ref boat> (m)" in reference-delta mode, else the channel name. */
  const getTimelineAxisLabel = (): string => {
    if (!referenceDeltaActive()) {return getChannelName();}
    const name = getReferenceBoatName();
    return name ? `Distance to ${name} (m)` : 'Distance to reference (m)';
  };

  // Get value extent across all sources -> final (padded) Y domain.
  // Normal mode: single 5% padding (matches previous behaviour). Delta mode: force 0 into the domain
  // so the reference boat's zero line is always visible, then pad the span by 5%.
  const yDomain = createMemo(() => {
    const groups = renderGroups();
    const deltaMode = referenceDeltaActive();
    if (groups.length === 0) {return deltaMode ? [-1, 1] : [0, 100];}

    let minVal = Infinity;
    let maxVal = -Infinity;

    for (const group of groups) {
      for (const d of group.data) {
        const val = getTimelineYValue(d);
        if (Number.isFinite(val)) {
          if (val < minVal) {minVal = val;}
          if (val > maxVal) {maxVal = val;}
        }
      }
    }

    // Ensure we have valid values
    if (!Number.isFinite(minVal) || !Number.isFinite(maxVal) || minVal === Infinity) {
      return deltaMode ? [-1, 1] : [0, 100];
    }

    if (deltaMode) {
      minVal = Math.min(minVal, 0);
      maxVal = Math.max(maxVal, 0);
      const span = maxVal - minVal || 1;
      const pad = span * 0.05;
      return [minVal - pad, maxVal + pad];
    }

    return [minVal * 0.95, maxVal * 1.05]; // Add 5% padding
  });

  // Helper function to get timestamp from data point
  const getTimestamp = (d) => {
    if (!d) {return new Date(0);}
    const timestamp = d.Datetime || d.timestamp || d.time || d.datetime;
    if (!timestamp) {return new Date(0);}
    if (timestamp instanceof Date) {return timestamp;}
    if (typeof timestamp === 'number') {return new Date(timestamp);}
    if (typeof timestamp === 'string') {
      const parsed = new Date(timestamp);
      if (!isNaN(parsed.getTime())) {return parsed;}
    }
    return new Date(0);
  };

  // Initialize SVG
  const initSVG = () => {
    if (!chartContainer) {return;}
    
    const bbox = chartContainer.getBoundingClientRect();
    // Add extra height for x-axis labels that extend below the axis
    const labelPadding = 20; // Extra space for axis labels
    const svgHeight = bbox.height + labelPadding;
    setDimensions({ width: bbox.width, height: bbox.height });
    
    // Remove existing SVG
    timeWindowBand?.destroy();
    timeWindowBand = null;
    d3.select(chartContainer).selectAll("svg").remove();
    
    svg = d3.select(chartContainer)
      .append("svg")
      .attr("width", bbox.width)
      .attr("height", svgHeight)
      .style("position", "absolute")
      .style("top", "0")
      .style("left", "0")
      .style("z-index", "10")
      .style("pointer-events", "auto");
    
    // Create scales
    const [minTime, maxTime] = timeExtent();
    const [minVal, maxVal] = yDomain();
    
    // Validate time extent
    if (!minTime || !maxTime || isNaN(minTime.getTime()) || isNaN(maxTime.getTime()) || minTime.getTime() === maxTime.getTime()) {
      logWarn('MultiMapTimeSeries: Invalid time extent, using default', {
        minTime: safeUtcDayFromDate(minTime),
        maxTime: safeUtcDayFromDate(maxTime)
      });
      const now = new Date();
      const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      xScale = d3.scaleTime()
        .domain([now, tomorrow])
        .range([margin.left, bbox.width - margin.right]);
    } else {
      xScale = d3.scaleTime()
        .domain([minTime, maxTime])
        .range([margin.left, bbox.width - margin.right]);
    }
    
    yScale = d3.scaleLinear()
      .domain([minVal, maxVal])
      .range([bbox.height - margin.bottom, margin.top]);

    const getThemeColor = (lightColor, darkColor) => {
      return themeStore.isDark() ? darkColor : lightColor;
    };
    
    // Add axes (let D3 auto-format time based on range, same as MapTimeSeries)
    const axiscolor = getThemeColor('#374151', '#cbd5e1');
    
    // Create axes with explicit styling
    const timezone = getTimezone();
    debug('🕐 MultiMapTimeSeries.initSVG: Using timezone for x-axis formatting', {
      timezone: timezone,
      timezoneType: typeof timezone,
      isNull: timezone === null,
      isUndefined: timezone === undefined
    });
    const xAxis = d3.axisBottom(xScale)
      .ticks(5)
      .tickFormat((d) => {
        if (d instanceof Date) {
          const formatted = formatTime(d, timezone);
          return formatted ?? formatTime(d) ?? "";
        }
        return String(d);
      });
    const yAxis = d3.axisLeft(yScale).ticks(5);
    
    // X-axis
    const xAxisGroup = svg.append("g")
      .attr("class", "axes")
      .attr("data-axis", "x")
      .attr("transform", `translate(0, ${bbox.height - margin.bottom})`)
      .call(xAxis);
    
    // Force style on all text elements - use each() to ensure it applies
    xAxisGroup.selectAll("text")
      .each(function() {
        d3.select(this)
          .attr("fill", axiscolor)
          .style("fill", axiscolor)
          .style("color", axiscolor)
          .style("opacity", 1);
      });
    xAxisGroup.selectAll("path, line")
      .style("stroke", axiscolor);
    
    // Y-axis
    const yAxisGroup = svg.append("g")
      .attr("class", "axes")
      .attr("data-axis", "y")
      .attr("transform", `translate(${margin.left}, 0)`)
      .call(yAxis);
    
    // Force style on all text elements - use each() to ensure it applies
    yAxisGroup.selectAll("text")
      .each(function() {
        d3.select(this)
          .attr("fill", axiscolor)
          .style("fill", axiscolor)
          .style("color", axiscolor)
          .style("opacity", 1);
      });
    yAxisGroup.selectAll("path, line")
      .style("stroke", axiscolor);
    
    // Add Y-axis label (similar to MapTimeSeries)
    const channel = getTimelineAxisLabel();
    const textColor = getThemeColor('#374151', '#cbd5e1'); // Use same color as axis
    svg.append("text")
      .attr("class", "axis-label")
      .attr("text-anchor", "left")
      .attr("transform", "translate(20,15)")
      .attr("font-size", "14px")
      .attr("fill", textColor)
      .text(channel);
    
    // Brush disabled - create simple clickable overlay for time selection only
    brush = null; // No brush object needed
    brushGroup = svg.append("g")
      .attr("class", "brush")
      .on("contextmenu", (event) => event.preventDefault());
    
    // Create clickable overlay rectangle (replaces brush overlay)
    const overlayWidth = bbox.width - margin.left - margin.right;
    const overlayHeight = bbox.height - margin.top - margin.bottom;
    brushGroup.append("rect")
      .attr("class", "overlay")
      .attr("x", margin.left)
      .attr("y", margin.top)
      .attr("width", overlayWidth)
      .attr("height", overlayHeight)
      .style("fill", "none")
      .style("pointer-events", "all")
      .style("cursor", "pointer")
      .on("click", (event) => {
        // Click to change selected time
        const [mouseX] = d3.pointer(event, svg.node());
        // xScale range already accounts for margin, so use mouseX directly
        const time = xScale.invert(mouseX);
        
        if (requestTimeControl('multimaptimeseries')) {
          setIsManualTimeChange(true);
          setSelectedTime(new Date(time), 'multimaptimeseries');
          if (onStableSelectedTimeChange) {
            onStableSelectedTimeChange(new Date(time));
          }
          
          setTimeout(() => {
            releaseTimeControl('multimaptimeseries');
          }, 100);
        }
        
        // Send filtered data if no active brush selection
        const hasRange = Array.isArray(selectedRange()) && selectedRange().length > 0;
        if (!hasRange) {
          const fullData = getFilteredData();
          safeOnMapUpdate(fullData);
        }
      })
      .on("dblclick", (event) => {
        // Double-click to clear selection
        event.preventDefault();
        event.stopPropagation();
        handleBrushClear();
      });

    // The playback time window as a band over the click overlay (under the playhead): drag its edges to
    // resize it, its body to slide it. This component is historical only (live mounts LiveMapTimeSeries);
    // the band also hides itself in live mode.
    timeWindowBand = mountTimeWindowBand(svg, {
      chartHeight: overlayHeight,
      top: margin.top,
      getXScale: () => xScale,
      timeControl: "multimaptimeseries",
      onTimeChange: (time) => onStableSelectedTimeChange?.(time),
    });

    // Playhead on top of overlay: red line + downward triangle, draggable (hover over line disables overlay click)
    const triangleSize = 8;
    const playheadY1 = 0; // Line starts at top (below triangle base)
    const playheadY2 = overlayHeight; // Full height to bottom of plot
    playheadGroup = svg.append("g")
      .attr("class", "playhead-group")
      .style("opacity", 1);

    playheadGroup.append("line")
      .attr("class", "mouse-line")
      .attr("stroke", "red")
      .attr("stroke-width", 2)
      .attr("x1", 0)
      .attr("x2", 0)
      .attr("y1", playheadY1)
      .attr("y2", playheadY2)
      .style("pointer-events", "none");

    playheadGroup.append("path")
      .attr("class", "playhead-triangle")
      .attr("d", `M ${-triangleSize / 2},0 L ${triangleSize / 2},0 L 0,${triangleSize} Z`)
      .attr("fill", "red")
      .attr("stroke", "red")
      .attr("stroke-width", 1)
      .style("pointer-events", "all");

    playheadGroup.append("rect")
      .attr("class", "playhead-drag-handle")
      .attr("x", -10)
      .attr("y", 0)
      .attr("width", 20)
      .attr("height", triangleSize + 4)
      .attr("fill", "transparent")
      .style("pointer-events", "all");

    playheadGroup.append("rect")
      .attr("class", "playhead-line-hit")
      .attr("x", -4)
      .attr("y", playheadY1)
      .attr("width", 8)
      .attr("height", playheadY2 - playheadY1)
      .attr("fill", "transparent")
      .style("pointer-events", "all");

    const playheadDrag = d3.drag()
      .on("start", () => {
        playheadGroup.classed("dragging", true);
      })
      .on("drag", (event) => {
        let mx: number;
        try {
          [mx] = d3.pointer(event, svg.node());
        } catch {
          return;
        }
        if (!Number.isFinite(mx)) {return;}
        const range = xScale.range();
        const x = Math.max(range[0], Math.min(range[1], mx));
        if (!Number.isFinite(x)) {return;}
        const time = new Date(xScale.invert(x));
        if (requestTimeControl('multimaptimeseries')) {
          setIsManualTimeChange(true);
          setSelectedTime(time, 'multimaptimeseries');
          if (onStableSelectedTimeChange) {onStableSelectedTimeChange(time);}
          playheadGroup.attr("transform", `translate(${x}, ${margin.top})`);
        }
      })
      .on("end", () => {
        playheadGroup.classed("dragging", false);
        setTimeout(() => releaseTimeControl('multimaptimeseries'), 100);
      });

    playheadGroup.call(playheadDrag);

    const initialTime = selectedTime() || (xScale.domain()[0]);
    const initialX = xScale(initialTime);
    if (Number.isFinite(initialX)) {
      playheadGroup.attr("transform", `translate(${initialX}, ${margin.top})`);
    }
    // Draw the band now that the playhead exists, so its right-edge layer goes above it.
    setTimeWindowBandEpoch((n) => n + 1);

    // Brush handlers disabled - only allow click to change selected time
    // brush.on("brush", brushed).on("end", brushEnded).on("start", null);

    // Brush selection restoration disabled - brush events are disabled
    // restoreBrushSelection();

  };

  // Helper function to restore brush selection
  const restoreBrushSelection = () => {
    if (!brushGroup || !xScale) {return;}
    
    const currentSelectedRange = selectedRange();
    const currentCutEvents = cutEvents();
    
    if (currentSelectedRange && currentSelectedRange.length > 0) {
      const rangeItem = currentSelectedRange[0];
      const startTime = new Date(rangeItem.start_time);
      const endTime = new Date(rangeItem.end_time);
      
      const x0 = xScale(startTime);
      const x1 = xScale(endTime);
      
      brushGroup.call(brush.move, [x0, x1]);
    } else if (currentCutEvents && currentCutEvents.length > 0) {
      brushGroup.call(brush.move, null);
    } else {
      brushGroup.call(brush.move, null);
    }
  };

  // Helper function to update time selection
  const updateTimeSelection = (time) => {
    if (requestTimeControl('multimaptimeseries')) {
      try { (window).skipBoatHaloOnce = true; } catch { /* window flag is best-effort */ }
      if (!isManualTimeChange()) {setIsManualTimeChange(true);}
      const newTime = new Date(time);
      const current = selectedTime();
      if (!(current instanceof Date) || Math.abs(current.getTime() - newTime.getTime()) > 0) {
        setSelectedTime(newTime, 'multimaptimeseries');
      }
      if (onStableSelectedTimeChange) {
        onStableSelectedTimeChange(newTime);
      }
      prevSelectedTime = newTime;
      
      setTimeout(() => {
        releaseTimeControl('multimaptimeseries');
      }, 100);
    }
  };

  // Helper function to handle brush clearing
  const handleBrushClear = () => {
    setSelectedRange([]);
    setSelectedRanges([]);
    setSelectedEvents([]);
    setHasSelection(false);
    setIsCut(cutEvents().length > 0);

    const nextWindow = fleetTimeWindowAfterBrush([], Number(timeWindow()));
    if (nextWindow !== Number(timeWindow())) {
      setTimeWindow(nextWindow);
    }
    
    const fullData = getFilteredData();
    
    if (fullData && fullData.length > 0) {
      lastMapUpdateSignature = null;
      const timestampedData = fullData.map((d, index) => ({
        ...d,
        _clearTimestamp: Date.now() + index
      }));
      
      safeOnMapUpdate(timestampedData);
    }
  };

  // Helper function to clear brush (brush is disabled, just clear selection state)
  const clearBrush = () => {
      if (isClearingBrush) {
        return;
      }
    
    isClearingBrush = true;
    
    try {
      // Brush is disabled, so no need to call brush.move
      // Just clear the selection state
      handleBrushClear();
    } finally {
      setTimeout(() => {
        isClearingBrush = false;
        isProgrammaticallyUpdatingBrush = false;
      }, 100);
    }
  };

  // Helper function to handle brush selection (matching MapTimeSeries pattern)
  const handleBrushSelection = async (x0, x1) => {
    const minSelectionMs = 1000;
    const selectionDuration = Math.abs(x1 - x0);

    if (selectionDuration > minSelectionMs) {
      const startTime = new Date(x0);
      const endTime = new Date(x1);
      if (!Number.isFinite(startTime.getTime()) || !Number.isFinite(endTime.getTime())) {
        debug('MultiMapTimeSeries: Brush selection skipped — non-finite time range', { x0, x1 });
        return;
      }
      
      // IMPORTANT: Always send FULL timeline data to map
      // MultiTrackLayer will handle brush filtering using selectedRange/selectedRanges
      // Do NOT filter data here - that causes props.data to be filtered
      const fullData = getFilteredData(); // Full timeline (filtered only by source/race/leg)
      
      lastMapUpdateSignature = null;
      const timestampedData = fullData.map((d, index) => ({
        ...d,
        _brushTimestamp: Date.now() + index
      }));
      
      safeOnMapUpdate(timestampedData);

      // Match MapTimeSeries pattern - convert Date objects to ISO strings for consistency
      const range = {"type": "range", "start_time": startTime.toISOString(), "end_time": endTime.toISOString()};
      // Playhead at the brush's newest end, so the trailing window lands inside a cut made from it.
      updateTimeSelection(x1);
      
      setSelectedRange([range]);
      setHasSelection(true);
      setIsCut(false);
      const nextWindow = fleetTimeWindowAfterBrush([range], Number(timeWindow()));
      if (nextWindow !== Number(timeWindow())) {
        setTimeWindow(nextWindow);
      }
    } else {
      updateTimeSelection(x0);
      
      setSelectedRange([]);
      setHasSelection(false);

      // IMPORTANT: Always send FULL timeline data to map
      const fullData = getFilteredData(); // Full timeline (filtered only by source/race/leg)
      
      lastMapUpdateSignature = null;
      const timestampedData = fullData.map((d, index) => ({
        ...d,
        _brushTimestamp: Date.now() + index
      }));

      safeOnMapUpdate(timestampedData);
    }
  };

  // Brush handler
  function brushed(event) {
    if (brushTimeout) {clearTimeout(brushTimeout);}
    
    brushTimeout = setTimeout(() => {
      if (isBrushActive) {return;}
      isBrushActive = true;
      
      try {
        if (event && event.selection) {
          const [x0, x1] = event.selection.map(xScale.invert);
          const selectionDuration = Math.abs(x1 - x0);
          // Newest end: the time window trails the playhead ([t − N, t]).
          updateTimeSelection(x1);
          
          // IMPORTANT: Always send FULL timeline data to map
          // MultiTrackLayer will handle brush filtering using selectedRange/selectedRanges
          // Do NOT filter data here - that causes props.data to be filtered
          if (selectionDuration > 1000) {
            const fullData = getFilteredData(); // Full timeline (filtered only by source/race/leg)
            
            lastMapUpdateSignature = null;
            const timestampedData = fullData.map((d, index) => ({
              ...d,
              _brushTimestamp: Date.now() + index
            }));
            
            safeOnMapUpdate(timestampedData);
          }
        }
      } finally {
        isBrushActive = false;
      }
    }, 100);
  }

  // Brush ended handler (matching MapTimeSeries pattern)
  function brushEnded(event) {
    if (isProgrammaticallyUpdatingBrush) {
      isProgrammaticallyUpdatingBrush = false;
      return;
    }
    
    if (!event || !event.selection) {
      handleBrushClear();
      if (brushGroup) {
        try {
          isProgrammaticallyUpdatingBrush = true;
          brushGroup.call(brush.move, null);
        } catch(e) { /* noop */ }
      }
    } else {
      const [x0, x1] = event.selection.map(xScale.invert);
      handleBrushSelection(x0, x1);
    }
  }

  // Update axes when scales change
  // In live mode: Updates axes smoothly without clearing/recreating (prevents flashing)
  const updateAxes = () => {
    if (!svg || !xScale || !yScale) {return;}
    
    const getThemeColor = (lightColor, darkColor) => {
      return themeStore.isDark() ? darkColor : lightColor;
    };
    const axiscolor = getThemeColor('#374151', '#cbd5e1');
    
    // Update x-axis (let D3 auto-format, same as MapTimeSeries)
    // Use transition for smooth updates in live mode
    const timezone = getTimezone();
    debug('🕐 MultiMapTimeSeries.updateAxes: Using timezone for x-axis formatting', {
      timezone: timezone,
      timezoneType: typeof timezone,
      isNull: timezone === null,
      isUndefined: timezone === undefined
    });
    const xAxis = d3.axisBottom(xScale)
      .ticks(5)
      .tickFormat((d) => {
        if (d instanceof Date) {
          const formatted = formatTime(d, timezone);
          return formatted ?? formatTime(d) ?? "";
        }
        return String(d);
      });
    const xAxisGroup = svg.select("g.axes[data-axis='x']");
    
    xAxisGroup.call(xAxis);
    
    // Force style on all text elements - use each() to ensure it applies
    xAxisGroup.selectAll("text")
      .each(function() {
        d3.select(this)
          .attr("fill", axiscolor)
          .style("fill", axiscolor)
          .style("color", axiscolor)
          .style("opacity", 1);
      });
    xAxisGroup.selectAll("path, line")
      .style("stroke", axiscolor);
    
    // Update y-axis
    const yAxis = d3.axisLeft(yScale).ticks(5);
    const yAxisGroup = svg.select("g.axes[data-axis='y']");
    
    yAxisGroup.call(yAxis);
    
    // Force style on all text elements - use each() to ensure it applies
    yAxisGroup.selectAll("text")
      .each(function() {
        d3.select(this)
          .attr("fill", axiscolor)
          .style("fill", axiscolor)
          .style("color", axiscolor)
          .style("opacity", 1);
      });
    yAxisGroup.selectAll("path, line")
      .style("stroke", axiscolor);
    
    // Update Y-axis label (remove old and add new if channel changed)
    const channel = getTimelineAxisLabel();
    const textColor = getThemeColor('#374151', '#cbd5e1');
    svg.selectAll("text.axis-label").remove(); // Remove existing label
    svg.append("text")
      .attr("class", "axis-label")
      .attr("text-anchor", "left")
      .attr("transform", "translate(60,20)")
      .attr("font-size", "14px")
      .attr("fill", textColor)
      .text(channel);
  };

  // Render all series
  const render = () => {
    if (!svg || !xScale || !yScale) {return;}

    const groups = renderGroups();

    // Update axes to reflect current scale domains
    updateAxes();


    // Gap threshold: 3 seconds (timeline chart only; map uses MultiTrackLayer's threshold).
    // Matches the reference-delta integration gap policy (TIMELINE_VMG_GAP_THRESHOLD_MS).
    const gapThresholdMs = TIMELINE_VMG_GAP_THRESHOLD_MS;
    const LEG_BOUNDARY_BRIDGE_MS = 10000; // Bridge gap at consecutive leg boundary (e.g. leg 0 -> 1) so line stays continuous

    const getLegNum = (p) => {
      const v = p?.leg_number ?? p?.Leg_number ?? p?.LEG;
      if (v === undefined || v === null) {return undefined;}
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    };

    // Helper: true = create gap (point undefined), false = no gap (point defined). Used as .defined((d,i,data) => !hasGap(d,i,data))
    const hasGap = (d, i, data) => {
      if (i === 0) {return false;} // Always define first point
      const prev = data[i - 1];
      const prevTime = new Date(prev.Datetime || prev.timestamp || prev.time).getTime();
      const currTime = new Date(d.Datetime || d.timestamp || d.time).getTime();
      const gap = currTime - prevTime;
      if (gap > gapThresholdMs) {
        const prevLeg = getLegNum(prev);
        const currLeg = getLegNum(d);
        const bridgeLegBoundary =
          prevLeg !== undefined &&
          currLeg !== undefined &&
          gap <= LEG_BOUNDARY_BRIDGE_MS &&
          Math.abs((currLeg ?? 0) - (prevLeg ?? 0)) === 1;
        if (bridgeLegBoundary) {return false;} // no gap at consecutive leg boundary
        return true; // gap
      }
      return false; // no gap
    };
    
    // Line generator with gap detection
    // Get x-axis domain to filter out points outside the scale bounds
    const xDomain = xScale.domain();
    const xMin = xDomain[0]?.getTime() ?? -Infinity;
    const xMax = xDomain[1]?.getTime() ?? Infinity;
    
    const lineGenerator = d3.line()
      .x((d) => xScale(new Date(d.Datetime || d.timestamp || d.time)))
      .y((d) => yScale(getTimelineYValue(d)))
      .defined((d, i, data) => {
        const val = getTimelineYValue(d);
        if (!Number.isFinite(val)) {return false;}
        
        // Check if timestamp is within x-axis domain bounds
        const timestamp = new Date(d.Datetime || d.timestamp || d.time).getTime();
        if (timestamp < xMin || timestamp > xMax) {return false;}
        
        // Create gap if time difference > 3 seconds
        return !hasGap(d, i, data);
      });
    
    // Render using SVG
    const paths = svg.selectAll("path.series-line")
      .data(groups, (d) => d.sourceId); // Key function for efficient updates
    
    // Exit: remove paths for deselected sources
    paths.exit().remove();
    
    // Enter: create new paths for new sources
    const pathsEnter = paths.enter()
      .append("path")
      .attr("class", "series-line")
      .attr("data-source-id", (d) => d.sourceId) // Add attribute for WebSocket updates
      .style("fill", "none")
      .style("stroke-width", "1px");
    
    // Highlight paths for hovered/selected boats (FleetMap)
    const highlighted = props.highlightedSourceIds ?? new Set<number>();
    const hasHighlight = highlighted.size > 0;

    // Update: merge enter and update selections (D3 enter-union-append pattern)
    pathsEnter.merge(paths)
      .attr("data-source-id", (d) => d.sourceId) // Ensure attribute is set
      .style("stroke", (d) => d.color)
      .style("stroke-width", (d) => (hasHighlight && highlighted.has(d.sourceId)) ? "3px" : "1px")
      .style("opacity", (d) => (hasHighlight && highlighted.has(d.sourceId)) ? 1 : (hasHighlight ? 0.3 : 1))
      .attr("d", (d) => {
        if (!d.data || d.data.length === 0) {return '';}
        return lineGenerator(d.data);
      });
    
    // Render cursor line
    renderCursor();
  };

  // Update playhead position for selectedTime (playhead group created in initSVG)
  const renderCursor = () => {
    if (!svg || !xScale) {return;}

    const currentTime = selectedTime();
    if (!currentTime) {return;}

    const x = xScale(currentTime);
    if (playheadGroup && Number.isFinite(x)) {
      playheadGroup.attr("transform", `translate(${x}, ${margin.top})`);
    }
  };

  // Handle resize
  const handleResize = () => {
    initSVG();
    render();
  };

  // ResizeObserver for when container gets dimensions (e.g. FleetVideo timeline); cleaned up in onCleanup
  let resizeObserver: ResizeObserver | null = null;

  // Mount
  onMount(() => {
    debug('🕐 MultiMapTimeSeries: onMount called');
    const initialTz = getCurrentDatasetTimezone();
    debug('🕐 MultiMapTimeSeries: Initial timezone on mount', {
      timezone: initialTz,
      isNull: initialTz === null,
      isUndefined: initialTz === undefined
    });
    
    window.addEventListener('resize', handleResize);
    
    if (chartContainer && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        if (chartContainer && groupedData().length > 0 && svg) {handleResize();}
      });
      resizeObserver.observe(chartContainer);
    }
    
    setTimeout(() => {
      fetchCombinedData().catch(e => logWarn('MultiMapTimeSeries: Mount fetch failed', e));
    }, 0);
  });

  // Re-fetch when dependencies change
  // Add loop detection to prevent infinite loops
  let lastFetchSignature = '';
  let fetchCount = 0;
  const MAX_FETCH_ATTEMPTS = 10;
  
  createEffect(() => {
    const _ids = getSelectedSourceIds();
    // Use selectedDate from persistantStore as the source of truth (matches MapContainer)
    const _date = persistantStore.selectedDate && persistantStore.selectedDate();
    // Include race/leg filters so map data refetches when filters change (FleetMap respects timeseries filters)
    const _races = (globalSelectedRaces && globalSelectedRaces()) || [];
    const _legs = (globalSelectedLegs && globalSelectedLegs()) || [];
    // Re-run when race options change (e.g. date/races loaded and set hour or race options) so we refetch with correct filter
    const _raceOptions = (raceOptions && raceOptions()) || [];
    const racesKey = Array.isArray(_races) ? _races.slice().sort().join(',') : '';
    const legsKey = Array.isArray(_legs) ? _legs.slice().sort().join(',') : '';
    const optionsKey = Array.isArray(_raceOptions) ? _raceOptions.slice().sort().join(',') : '';
    
    // Create a signature to detect actual changes
    const signature = `${_ids instanceof Set ? Array.from(_ids).sort().join(',') : ''}_${_date || ''}_${racesKey}_${legsKey}_${optionsKey}`;
    
    // Only fetch if signature changed (prevent infinite loops)
    if (signature !== lastFetchSignature) {
      lastFetchSignature = signature;
      fetchCount = 0;
      // Use setTimeout to ensure fetch is async and doesn't block effect execution
      setTimeout(() => {
        void fetchCombinedData();
      }, 0);
    } else {
      fetchCount++;
      if (fetchCount > MAX_FETCH_ATTEMPTS) {
        logWarn('MultiMapTimeSeries: Detected potential infinite loop, stopping fetches', {
          signature,
          fetchCount
        });
        return;
      }
    }
  });

  // Initialize/re-initialize SVG when data is available
  // Update scales when time/value extents change
  createEffect(() => {
    const [minTime, maxTime] = timeExtent();
    // yDomain() is already the final padded domain (and forces 0 in reference-delta mode).
    const [minVal, maxVal] = yDomain();

    if (xScale && yScale && svg) {
      let scaleChanged = false;

      // Update scale domains
      if (minTime && maxTime && !isNaN(minTime.getTime()) && !isNaN(maxTime.getTime()) && minTime.getTime() !== maxTime.getTime()) {
        const oldDomain = xScale.domain();
        xScale.domain([minTime, maxTime]);
        const newDomain = xScale.domain();

        // Check if domain actually changed
        if (oldDomain.length !== newDomain.length ||
            oldDomain[0].getTime() !== newDomain[0].getTime() ||
            oldDomain[1].getTime() !== newDomain[1].getTime()) {
          scaleChanged = true;
        }
      }

      if (Number.isFinite(minVal) && Number.isFinite(maxVal) && minVal !== maxVal) {
        const oldY = yScale.domain();
        yScale.domain([minVal, maxVal]);
        if (oldY.length !== 2 || oldY[0] !== minVal || oldY[1] !== maxVal) {
          scaleChanged = true;
        }
      }

      // Update axes smoothly (with transition in live mode)
      updateAxes();

      // Re-render when scale changes
      if (scaleChanged) {
        render();
      }
    }
  });

  // Effect: Initialize/re-render when data changes
  createEffect(() => {
    const groups = groupedData();
    
    if (groups.length > 0 && chartContainer) {
      const tryInit = () => {
        const bbox = chartContainer.getBoundingClientRect();
        if (bbox.width > 0 && bbox.height > 0) {
          if (!svg) {initSVG();}
          render();
        } else {
          requestAnimationFrame(tryInit);
        }
      };
      setTimeout(tryInit, 50);
    }
  });

  // Re-render when reference-delta mode toggles or the enriched (delta) groups change.
  // groupedData() is unchanged when only the reference boat prop changes, so the data-change
  // effect above would not fire — track renderGroups()/referenceDeltaActive() explicitly here.
  createEffect(() => {
    const _groups = renderGroups();
    const _active = referenceDeltaActive();
    const [minVal, maxVal] = yDomain();
    if (svg && xScale && yScale) {
      if (Number.isFinite(minVal) && Number.isFinite(maxVal) && minVal !== maxVal) {
        yScale.domain([minVal, maxVal]);
      }
      render();
    }
  });

  // Time-window band: follows the playhead and the window length (both read in update()).
  createEffect(() => {
    timeWindowBandEpoch();
    timeWindowBand?.update();
  });

  // Cleanup
  onCleanup(() => {
    timeWindowBand?.destroy();
    timeWindowBand = null;
    window.removeEventListener('resize', handleResize);
    if (resizeObserver && chartContainer) {
      try { resizeObserver.unobserve(chartContainer); } catch (_) { /* observer may already be gone */ }
      resizeObserver = null;
    }
  });

  // Re-render cursor when selectedTime changes
  createEffect(() => {
    const _time = selectedTime();
    if (_time && svg) {
      renderCursor();
    }
  });

  // Update path opacity/stroke when fleet map boat hover/selection changes (render() is not re-run on hover)
  createEffect(() => {
    const highlighted = props.highlightedSourceIds ?? new Set<number>();
    if (!svg) {return;}
    const hasHighlight = highlighted.size > 0;
    svg.selectAll("path.series-line")
      .style("stroke-width", (d: { sourceId: number }) => (hasHighlight && highlighted.has(d.sourceId)) ? "3px" : "1px")
      .style("opacity", (d: { sourceId: number }) => (hasHighlight && highlighted.has(d.sourceId)) ? 1 : (hasHighlight ? 0.3 : 1));
  });

  // Send filtered data to map whenever sources, races, or legs change
  // NOTE: MultiMapTimeSeries always sends the full timeline data (filtered only by source/race/leg)
  // Time window filtering is handled by MultiTrackLayer when rendering the map tracks
  createEffect(() => {
    const groups = groupedData(); // Track groupedData to trigger on source/race/leg changes
    const filteredData = getFilteredData(); // Get filtered data (by source/race/leg only, no time window)
    // Track data length to detect when data is refetched after source changes
    // This ensures map updates both when sources change AND when new data arrives
    const dataLength = combinedData().length;
    
    // Create a signature of current filters to detect actual changes
    const currentIds = getSelectedSourceIds();
    const races = Array.isArray(props.selectedRacesTimeseries) ? props.selectedRacesTimeseries : (Array.isArray(props.selectedRaces) ? props.selectedRaces : (globalSelectedRaces && globalSelectedRaces()));
    const legs = Array.isArray(props.selectedLegsTimeseries) ? props.selectedLegsTimeseries : (Array.isArray(props.selectedLegs) ? props.selectedLegs : (globalSelectedLegs && globalSelectedLegs()));
    const playing = isPlaying();
    const hasRange = Array.isArray(selectedRange()) && selectedRange().length > 0;
    // Filter signature includes source/race/leg filters AND data length (to detect refetches)
    const filterSignature = `${currentIds instanceof Set ? Array.from(currentIds).sort().join(',') : ''}_${Array.isArray(races) ? races.join(',') : ''}_${Array.isArray(legs) ? legs.join(',') : ''}_${hasRange}_${dataLength}`;
    
    // Don't send data if selectedSourceIds is empty AND we have no data yet (still initializing)
    // But if we have data and sources are empty, send all data (MultiTrackLayer will handle it)
    // NOTE: Historical data query happens ONLY in onMount, not in reactive effects
    if (currentIds instanceof Set && currentIds.size === 0 && dataLength === 0) {
      return;
    }
    
      // Only reset time and send data if filters actually changed (not just a re-run of the effect)
      // OR if data just became available (dataLength changed from 0 to >0)
      // This prevents unnecessary data sends when only timeWindow changes
      const filtersChanged = filterSignature !== lastFilterSignature;
      const dataJustLoaded = dataLength > 0 && lastFilterSignature === '';
      
      if (filtersChanged || dataJustLoaded) {
        lastFilterSignature = filterSignature;

        const raceLegSignature = `${Array.isArray(races) ? races.join(",") : ""}|${Array.isArray(legs) ? legs.join(",") : ""}`;
        const raceLegChanged = raceLegSignature !== lastRaceLegSignature;
        if (raceLegChanged) {
          lastRaceLegSignature = raceLegSignature;
          if (!hasRange && !playing && filteredData.length > 0) {
            // Race/leg chip changed: park the playhead 2 min into the newly filtered race.
            repositionFleetPlaybackForFilteredData(
              filteredData,
              "multimaptimeseries",
              FLEET_PLAYBACK_START_OFFSET_MS
            );
          }
        }
        
        // Reset time to start + time window when playback time was never initialized
        if (!hasRange && !playing && filteredData.length > 0 && isUnsetPlaybackTime(selectedTime())) {
          initializeSelectedTimeFromData(filteredData);
        }
        
        // Send data to map when filters change OR when data first loads (including [] when no sources selected)
        // Time window filtering is handled by MultiTrackLayer, so we don't need to send data when only timeWindow changes
        safeOnMapUpdate(filteredData);
        if (!isVideoContext() && filteredData.length > 0) {
          debug('MultiMapTimeSeries: Reactive effect sending data to map', {
            reason: dataJustLoaded ? 'initial data load' : 'filters changed',
            filteredDataLength: filteredData.length,
            filterSignature
          });
        }
      }
  });

  return (
    <div
      ref={(el) => (chartContainer = el)}
      class="chart maptimeseries-chart"
      style={{"width":"100%","height":"100%","background":"var(--color-bg-card)"}}
    />
  );
}
